import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { NovaAvatar } from '../common/NovaAvatar';
import { getRouteIntro, pickIntroVariantIndex } from './route-context-intros';
import {
  getEmployerRouteIntro,
  type EmployerRouteIntroKey,
} from './route-context-intros.employer';
import type { PageContextScreen } from '../../lib/page-context';

/** Per-character cadence. ~90 chars lands a little under two seconds. */
const TYPE_INTERVAL_MS = 18;

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Reveals `text` one character at a time, and reports when it is done so the
 * caret can go. Falls straight through to the full string when the user
 * has asked for reduced motion, and clears its timer on unmount / text change
 * so a screen switch mid-type cannot leave an interval running.
 */
function useTypewriter(text: string): { shown: string; done: boolean } {
  const [count, setCount] = useState(() => (prefersReducedMotion() ? text.length : 0));
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setCount(text.length);
      return;
    }

    setCount(0);
    timerRef.current = setInterval(() => {
      setCount((current) => {
        if (current >= text.length) {
          if (timerRef.current) clearInterval(timerRef.current);
          timerRef.current = null;
          return current;
        }
        return current + 1;
      });
    }, TYPE_INTERVAL_MS);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [text]);

  return { shown: text.slice(0, count), done: count >= text.length };
}

/**
 * The "here is what I can do on this screen" block, docked above the composer.
 *
 * Deliberately UI-only: it is styled like an assistant message but never
 * enters the assistant-ui runtime, the thread history or the backend. Making
 * it a real message would bill an AI turn on every navigation to /cv or
 * /jobs, which is the wrong trade for chrome copy.
 *
 * Message only: the catalog's suggestion chips are not rendered here, so the
 * composer stays the one place to start a turn.
 */
export const RouteContextIntro: FC<{
  screen: PageContextScreen | EmployerRouteIntroKey;
  /** Which intro catalog + i18n namespace to resolve against. */
  mode?: 'seeker' | 'employer';
  pathname?: string;
}> = ({ screen, mode = 'seeker', pathname }) => {
  const { t } = useTranslation(['seeker', 'employer', 'common']);
  const ns = mode === 'employer' ? 'employer' : 'seeker';
  const intro =
    mode === 'employer'
      ? getEmployerRouteIntro(screen as EmployerRouteIntroKey)
      : getRouteIntro(screen as PageContextScreen, pathname);

  // Re-picked only when the screen changes, so re-renders (streaming, resize)
  // don't reshuffle the copy mid-read.
  const messageKey = useMemo(() => {
    if (!intro || intro.messageKeys.length === 0) return null;
    const index = pickIntroVariantIndex(intro.messageKeys[0] ?? screen, intro.messageKeys.length);
    return intro.messageKeys[index] ?? intro.messageKeys[0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, pathname]);

  const message = messageKey ? t(`${ns}:${messageKey}` as never) : '';
  const { shown, done } = useTypewriter(message);

  if (!intro || !messageKey) return null;

  return (
    <div className="tabbio-route-intro">
      <div className="tabbio-route-intro-head">
        <NovaAvatar size={24} />
        <span className="tabbio-route-intro-name">
          {t('common:assistantName', { defaultValue: 'Career agent' })}
        </span>
      </div>

      {/* The full string is laid out invisibly to fix the block's height from
          the first frame, with the typed text overlaid on top. Growing the
          box character-by-character would re-centre the greeting above and
          make the whole column jiggle for the length of the animation.

          aria-live is deliberately absent: the typewriter would announce
          every frame. The finished string is exposed once via aria-label. */}
      <p className="tabbio-route-intro-body" dir="auto" aria-label={message}>
        <span className="tabbio-route-intro-ghost" aria-hidden>
          {message}
        </span>
        <span className="tabbio-route-intro-typed" aria-hidden>
          {shown}
          {!done ? <span className="tabbio-route-intro-caret" /> : null}
        </span>
      </p>
    </div>
  );
};
